import type { FilterValues } from '@nucleus-commerce/admin-kit/index-table';
import type { PickerSelection } from '@nucleus-commerce/admin-kit/resource-picker';
import type {
  ProductFilters,
  ProductFiltersSearchBy,
  ProductSelection,
} from '@nucleus-commerce/api-client/admin';

import { toProductFilters, type ProductScopeKind } from './product-filters';

/** Ids the endpoints accept in one selection (`MAX_SELECTION_PRODUCTS` on the backend). */
export const MAX_SELECTION_PRODUCTS = 5000;

/** What the picker fixed for its caller, on top of what the user filtered. */
export interface ProductSelectionPins {
  /**
   * Filters the caller pins (`status` from `statuses`) and the membership exclusion
   * (`notInStoreId` for `memberOf: store`). They win over a picked filter of the same name.
   */
  filters?: ProductFilters;
  /** Ids the picker never lists (`excludeIds`): always excluded from a query selection. */
  excludeIds?: readonly string[];
}

const SEARCH_BY = new Set<string>(['all', 'title', 'sku', 'barcode', 'id']);

/**
 * The picker's result as the `ProductSelection` the bulk endpoints take. Id mode passes the ids
 * through. "All matching" becomes a query selection: the filters in JSON form, the search text,
 * the pinned filters, `includeIds` / `excludeIds`, and `expectedCount` = the total the picker
 * showed (the server answers 409 SELECTION_CHANGED when its count differs).
 */
export function toProductSelection(
  selection: Pick<PickerSelection<unknown>, 'mode' | 'ids' | 'query'>,
  scope: ProductScopeKind,
  pins: ProductSelectionPins = {},
): ProductSelection {
  if (selection.mode === 'ids' || !selection.query) {
    return { mode: 'ids', ids: [...selection.ids] };
  }
  const { query } = selection;
  const searchBy =
    query.searchBy && SEARCH_BY.has(query.searchBy)
      ? (query.searchBy as ProductFiltersSearchBy)
      : undefined;
  const filters: ProductFilters = {
    ...toProductFilters(query.filters, scope),
    ...(query.q ? { q: query.q, ...(searchBy && searchBy !== 'all' ? { searchBy } : {}) } : {}),
    ...pins.filters,
  };
  const excludeIds = [...new Set([...query.excludeIds, ...(pins.excludeIds ?? [])])];
  return {
    mode: 'query',
    scope,
    filters,
    ...(query.includeIds.length > 0 ? { includeIds: [...query.includeIds] } : {}),
    ...(excludeIds.length > 0 ? { excludeIds } : {}),
    expectedCount: query.total,
  };
}

/** What a product list screen's URL holds: its applied filters and its search text. */
export interface ProductListQuery {
  filters: FilterValues;
  q?: string | undefined;
}

/**
 * A list screen's "Select all N" (the index table's `exclude` descriptor) as a query selection
 * over the list's own filters and search: the unticked rows in `excludeIds`, `expectedCount` =
 * the total the table showed.
 */
export function toListSelection(
  descriptor: { ids: readonly string[]; total: number },
  list: ProductListQuery,
  scope: ProductScopeKind,
): ProductSelection {
  return toProductSelection(
    {
      mode: 'query',
      ids: [],
      query: {
        q: list.q ?? '',
        searchBy: undefined,
        filters: list.filters,
        sort: undefined,
        includeIds: [],
        excludeIds: [...descriptor.ids],
        count: descriptor.total - descriptor.ids.length,
        total: descriptor.total,
      },
    },
    scope,
  );
}
